import { useState, useEffect, useRef } from 'react';
import api from '../api';

// Beschrijvende titels per sectie (alle bekende frameworks)
const SECTION_TITLES = {
  // ISO 27001 — normatieve clausules
  '§4':  'Context van de organisatie',
  '§5':  'Leiderschap',
  '§6':  'Planning',
  '§7':  'Ondersteuning',
  '§8':  'Uitvoering',
  '§9':  'Evaluatie van prestaties',
  '§10': 'Verbetering',
  // ISO 27001 — Bijlage A
  'A.5': 'Organisatorische maatregelen',
  'A.6': 'Personeelsmaatregelen',
  'A.7': 'Fysieke maatregelen',
  'A.8': 'Technologische maatregelen',
  // AVG / GDPR
  'Artikel 5':   'Beginselen inzake verwerking van persoonsgegevens',
  'Artikel 6':   'Rechtsgrond voor verwerking',
  'Artikel 7':   'Voorwaarden voor toestemming',
  'Artikel 9':   'Bijzondere categorieën persoonsgegevens',
  'Artikel 13':  'Informatieplicht bij directe verzameling',
  'Artikel 14':  'Informatieplicht bij indirecte verzameling',
  'Artikel 17':  'Recht op wissing',
  'Artikel 25':  'Gegevensbescherming door ontwerp en standaardinstellingen',
  'Artikel 28':  'Verwerkers en verwerkersovereenkomst',
  'Artikel 30':  'Register van verwerkingsactiviteiten',
  'Artikel 32':  'Beveiliging van verwerking',
  'Artikel 33':  'Melding van inbreuk aan toezichthouder',
  'Artikel 34':  'Mededeling van inbreuk aan betrokkene',
  'Artikel 35':  'Gegevensbeschermingseffectbeoordeling (DPIA)',
  'Artikel 37':  'Functionaris voor gegevensbescherming (FG/DPO)',
  'Hoofdstuk V': 'Doorgifte aan derde landen',
  // NIS2
  'Artikel 3':   'Identificatie en registratie',
  'Artikel 20':  'Governance en leidinggevend orgaan',
  'Artikel 21':  'Cyberbeveiligingsmaatregelen',
  'Artikel 23':  'Meldplicht incidenten',
};

function SectionCheckbox({ sectionControls, selectedIds, onToggleSection }) {
  const ref = useRef();
  const total   = sectionControls.length;
  const checked = sectionControls.filter(c => selectedIds.has(c.id)).length;
  const allOn   = checked === total;
  const someOn  = checked > 0 && checked < total;

  useEffect(() => {
    if (ref.current) ref.current.indeterminate = someOn;
  }, [someOn]);

  return (
    <input
      ref={ref}
      type="checkbox"
      className="form-check-input"
      checked={allOn}
      onChange={() => onToggleSection(sectionControls, !allOn)}
    />
  );
}

/**
 * ScopeSelector
 * Props:
 *   frameworkId       – welk framework
 *   selectedIds       – Set<number> (controlled, van parent)
 *   setSelectedIds    – setter van parent
 *   defaultSelectAll  – true (default) = alles aanvinken bij laden
 *                       false = selectedIds van parent overnemen (voor edit-modus)
 */
export default function ScopeSelector({ frameworkId, selectedIds, setSelectedIds, defaultSelectAll = true }) {
  const [controls, setControls] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [open, setOpen]         = useState({});

  useEffect(() => {
    if (!frameworkId) return;
    setLoading(true);
    api.get(`/api/frameworks/${frameworkId}/controls`)
      .then(r => {
        setControls(r.data);
        if (defaultSelectAll) {
          setSelectedIds(new Set(r.data.map(c => c.id)));
        }
        // Standaard dichtgeklapt
        const openState = {};
        r.data.forEach(c => { openState[c.section] = false; });
        setOpen(openState);
      })
      .finally(() => setLoading(false));
  }, [frameworkId]); // setSelectedIds en defaultSelectAll zijn stabiel

  if (loading) return (
    <div className="text-center text-muted py-4">
      <div className="spinner-border spinner-border-sm me-2" />Laden…
    </div>
  );

  const sections = [];
  const bySection = {};
  controls.forEach(c => {
    if (!bySection[c.section]) { bySection[c.section] = []; sections.push(c.section); }
    bySection[c.section].push(c);
  });

  const totalSelected = selectedIds.size;
  const totalControls = controls.length;

  function toggleSection(sectionControls, select) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      sectionControls.forEach(c => select ? next.add(c.id) : next.delete(c.id));
      return next;
    });
  }

  function toggleControl(id) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <span className="text-muted small">
          <strong>{totalSelected}</strong> / {totalControls} controls geselecteerd
        </span>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-sm btn-outline-secondary"
            onClick={() => setSelectedIds(new Set(controls.map(c => c.id)))}>
            Alles selecteren
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary"
            onClick={() => setSelectedIds(new Set())}>
            Alles deselecteren
          </button>
        </div>
      </div>

      <div className="border rounded overflow-hidden">
        {sections.map((section, i) => {
          const sControls   = bySection[section];
          const checkedCount = sControls.filter(c => selectedIds.has(c.id)).length;
          const isOpen       = open[section] ?? true;

          return (
            <div key={section} className={i > 0 ? 'border-top' : ''}>
              <div className="d-flex align-items-center gap-2 px-3 py-2"
                style={{ background: '#f8fafc', cursor: 'pointer', userSelect: 'none' }}>
                <span onClick={() => setOpen(o => ({ ...o, [section]: !o[section] }))}
                  style={{ width: 16, color: '#64748b', flexShrink: 0 }}>
                  <i className={`bi bi-chevron-${isOpen ? 'down' : 'right'}`} style={{ fontSize: 11 }} />
                </span>
                <div className="form-check mb-0">
                  <SectionCheckbox
                    sectionControls={sControls}
                    selectedIds={selectedIds}
                    onToggleSection={toggleSection}
                  />
                </div>
                <span className="fw-semibold small flex-grow-1"
                  onClick={() => setOpen(o => ({ ...o, [section]: !o[section] }))}>
                  {section}
                  {SECTION_TITLES[section] && (
                    <span className="fw-normal text-muted ms-2">{SECTION_TITLES[section]}</span>
                  )}
                </span>
                <span className="text-muted small">{checkedCount}/{sControls.length}</span>
              </div>

              {isOpen && (
                <div className="px-3 pb-2 pt-1">
                  {sControls.map(c => (
                    <div key={c.id} className="form-check mb-1 ms-4">
                      <input className="form-check-input" type="checkbox" id={`ctrl-${c.id}`}
                        checked={selectedIds.has(c.id)} onChange={() => toggleControl(c.id)} />
                      <label className="form-check-label small" htmlFor={`ctrl-${c.id}`}>
                        <span className="font-mono text-muted me-2">{c.control_id}</span>
                        {c.title}
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
